<template>
  <div class="supplier-signup">
    <div class="supplier-signup__page">
      <div class="hero hero--edit">
        <span class="hero__eyebrow">Your Profile</span>
        <h1 class="hero__title">Edit Basic Information</h1>
        <p class="hero__subtitle">
          Update your company details, questionnaires, and certificates. Products, materials, and
          services are managed separately from your
          <router-link :to="{ name: 'Profile' }" class="hero__link">My Profile</router-link> page.
        </p>
      </div>

      <div class="supplier-signup__container">
        <div v-if="loading" class="supplier-signup__loading">
          <span class="loading-spinner"></span>
          Loading your profile…
        </div>

        <template v-else-if="loadError">
          <div class="rform__error supplier-signup__top-error">
            <i class="fa fa-triangle-exclamation"></i>
            {{ loadError }}
          </div>
        </template>

        <template v-else>
          <!-- ============ Tabs ============ -->
          <nav class="edit-tabs">
            <button v-for="tab in tabs" :key="tab.key" type="button" class="edit-tab"
              :class="{ 'is-active': activeTab === tab.key }" @click="activeTab = tab.key">
              <span class="edit-tab__icon">{{ tab.icon }}</span>
              <span>{{ tab.label }}</span>
              <span v-if="tab.count !== undefined" class="edit-tab__count">{{ tab.count }}</span>
            </button>
          </nav>

          <!-- ================================================================
               TAB 1 — Basic Information Form
               ================================================================ -->
          <section v-show="activeTab === 'basic'">
            <!-- Business types -->
            <div class="section-block section-block--types">
              <div class="section-head">
                <span class="section-head__icon">🧭</span>
                <div>
                  <h4 class="section-head__title">What best describes you?</h4>
                  <p class="section-head__desc">Select all that apply — this shapes the rest of the form.</p>
                </div>
              </div>

              <div class="type-select">
                <label v-for="opt in businessTypeOptions" :key="opt.value" class="type-card"
                  :class="{ 'is-active': businessTypes.includes(opt.value) }">
                  <input type="checkbox" :value="opt.value" v-model="businessTypes" class="type-card__input"
                    name="types[]" :id="`type_${opt.value}`" />
                  <span class="type-card__icon">{{ opt.icon }}</span>
                  <span class="type-card__label">{{ opt.label }}</span>
                  <span class="type-card__desc">{{ opt.desc }}</span>
                </label>
              </div>
              <span v-if="fieldError('types')" class="rform__error">{{ fieldError('types') }}</span>
            </div>

            <ReusableForm id="edit_basic_info_form" :loading="submitting.basic" submit-label="Save Basic Info"
              :show-cancel="false" @submit.prevent="handleBasicSubmit">
              <div v-if="topError.basic" class="rform__error supplier-signup__top-error">
                <i class="fa fa-triangle-exclamation"></i>
                {{ topError.basic }}
              </div>

              <!-- Company Information -->
              <div class="section-block section-block--company">
                <div class="section-head">
                  <span class="section-head__icon">🏢</span>
                  <div>
                    <h4 class="section-head__title">Company Information</h4>
                    <p class="section-head__desc">The basics — who you are and how to reach you.</p>
                  </div>
                </div>

                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">Company Name</label>
                    <input v-model="form.company_name" type="text" class="rform__input"
                      :class="{ 'is-invalid': fieldError('company_name') }" placeholder="Enter company name"
                      id="company_name" name="company_name" />
                    <span v-if="fieldError('company_name')" class="rform__error">{{ fieldError('company_name') }}</span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">Email</label>
                    <input v-model="form.email" type="email" class="rform__input"
                      :class="{ 'is-invalid': fieldError('email') }" placeholder="Enter email" id="email"
                      name="email" />
                    <span v-if="fieldError('email')" class="rform__error">{{ fieldError('email') }}</span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">Phone</label>
                    <input v-model="form.phone" type="tel" class="rform__input"
                      :class="{ 'is-invalid': fieldError('phone') }" placeholder="Enter phone" id="phone"
                      name="phone" />
                    <span v-if="fieldError('phone')" class="rform__error">{{ fieldError('phone') }}</span>
                  </div>
                </div>

                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">Address</label>
                    <input v-model="form.physical_address" type="text" class="rform__input"
                      :class="{ 'is-invalid': fieldError('physical_address') }" placeholder="Enter address"
                      id="physical_address" name="physical_address" />
                    <span v-if="fieldError('physical_address')" class="rform__error">{{ fieldError('physical_address')
                      }}</span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">City</label>
                    <input v-model="form.street_city" type="text" class="rform__input"
                      :class="{ 'is-invalid': fieldError('street_city') }" placeholder="Enter city" id="street_city"
                      name="street_city" />
                    <span v-if="fieldError('street_city')" class="rform__error">{{ fieldError('street_city') }}</span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">State / Province</label>
                    <input v-model="form.state" type="text" class="rform__input"
                      :class="{ 'is-invalid': fieldError('state') }" placeholder="Enter state" id="state"
                      name="state" />
                    <span v-if="fieldError('state')" class="rform__error">{{ fieldError('state') }}</span>
                  </div>
                </div>

                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">Postal Code</label>
                    <input v-model="form.postal_code" type="text" class="rform__input" placeholder="Postal / ZIP code"
                      id="postal_code" name="postal_code" />
                    <span v-if="fieldError('postal_code')" class="rform__error">{{ fieldError('postal_code') }}</span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">Logo</label>
                    <div v-if="existingLogoUrl && !form.logo" class="current-file">
                      <img :src="existingLogoUrl" alt="Current logo" class="current-file__thumb" />
                      <span class="current-file__label">Current logo</span>
                    </div>
                    <input type="file" class="rform__input" accept="image/*" @change="onSimpleFile($event, 'logo')"
                      id="logo" name="logo" />
                    <span class="rform__help">200 × 60 px — leave blank to keep your current logo</span>
                    <span v-if="fieldError('logo')" class="rform__error">{{ fieldError('logo') }}</span>
                  </div>
                </div>
              </div>

              <!-- Manufacturer-only -->
              <div v-if="businessTypes.includes('manufacturer')" class="section-block section-block--manufacturing">
                <div class="section-head">
                  <span class="section-head__icon">🏭</span>
                  <div>
                    <h4 class="section-head__title">Manufacturing Details</h4>
                    <p class="section-head__desc">How your products are manufactured.</p>
                    <span class="section-head__tag section-head__tag--manufacturing">🏭 Manufacturers only</span>
                  </div>
                </div>
                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">Manufacturing Site</label>
                    <div class="rform__select-wrap">
                      <select v-model="form.manufacturing_type" class="rform__select"
                        :class="{ 'is-invalid': fieldError('manufacturing_type') }" id="manufacturing_type"
                        name="manufacturing_type">
                        <option value="">Select manufacturing site</option>
                        <option value="internal">Internal</option>
                        <option value="external">External</option>
                      </select>
                    </div>
                    <span v-if="fieldError('manufacturing_type')" class="rform__error">{{
                      fieldError('manufacturing_type') }}</span>
                  </div>
                  <div v-if="form.manufacturing_type === 'external'" class="rform__field">
                    <label class="rform__label">Manufacturer Category</label>
                    <div class="rform__select-wrap">
                      <select v-model="form.product_brand_type" class="rform__select"
                        :class="{ 'is-invalid': fieldError('product_brand_type') }" id="product_brand_type"
                        name="product_brand_type">
                        <option value="">Select category</option>
                        <option value="private_label">Private Label</option>
                        <option value="co_mfg">Co Manufacturer</option>
                        <option value="external_brand">External Brand</option>
                      </select>
                    </div>
                    <span v-if="fieldError('product_brand_type')" class="rform__error">{{
                      fieldError('product_brand_type') }}</span>
                  </div>
                  <div v-if="form.manufacturing_type === 'external'" class="rform__field">
                    <label class="rform__label">Manufacturer Type</label>
                    <div class="rform__select-wrap">
                      <select v-model="form.external_type" class="rform__select"
                        :class="{ 'is-invalid': fieldError('external_type') }" id="external_type" name="external_type">
                        <option value="">Select manufacturer type</option>
                        <option value="inland">Inland Manufacturer</option>
                        <option value="overseas">Overseas</option>
                        <option value="cross-border">Cross Border Manufacturer</option>
                      </select>
                    </div>
                    <span v-if="fieldError('external_type')" class="rform__error">{{ fieldError('external_type')
                      }}</span>
                  </div>
                </div>
              </div>

              <!-- Supplier-only production site -->
              <div v-if="businessTypes.includes('supplier')" class="section-block section-block--production">
                <div class="section-head">
                  <span class="section-head__icon">🚚</span>
                  <div>
                    <h4 class="section-head__title">Production Site Info</h4>
                    <p class="section-head__desc">Where the goods you supply are actually produced.</p>
                    <span class="section-head__tag section-head__tag--production">🌾 Suppliers only</span>
                  </div>
                </div>
                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">Manufacturing City</label>
                    <input v-model="form.production_city" type="text" class="rform__input" id="production_city"
                      name="production_city" />
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">Manufacturing Province/State</label>
                    <input v-model="form.production_state" type="text" class="rform__input" id="production_state"
                      name="production_state" />
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">Manufacturing Address</label>
                    <input v-model="form.production_address" type="text" class="rform__input" id="production_address"
                      name="production_address" />
                  </div>
                </div>
                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">Manufacturing Postal Code</label>
                    <input v-model="form.production_postcode" type="text" class="rform__input" id="production_postcode"
                      name="production_postcode" />
                  </div>
                </div>
              </div>

              <!-- POC -->
              <div class="section-block section-block--poc">
                <div class="section-head">
                  <span class="section-head__icon">👤</span>
                  <div>
                    <h4 class="section-head__title">Primary Point of Contact</h4>
                    <p class="section-head__desc">Who should we reach out to first?</p>
                  </div>
                </div>
                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">Emergency Contact Number</label>
                    <input v-model="form.emergency_contact" type="text" class="rform__input" id="emergency_contact"
                      name="emergency_contact" />
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">POC Name</label>
                    <input v-model="form.poc" type="text" class="rform__input"
                      :class="{ 'is-invalid': fieldError('poc') }" id="poc" name="poc" />
                    <span v-if="fieldError('poc')" class="rform__error">{{ fieldError('poc') }}</span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">Title</label>
                    <input v-model="form.title" type="text" class="rform__input" id="title" name="title" />
                  </div>
                </div>
              </div>

              <!-- QA -->
              <div class="section-block section-block--qa">
                <div class="section-head">
                  <span class="section-head__icon">🔬</span>
                  <div>
                    <h4 class="section-head__title">QA Contact</h4>
                    <p class="section-head__desc">Who handles quality and compliance questions.</p>
                  </div>
                </div>
                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">QA Name</label>
                    <input v-model="form.qa_name" type="text" class="rform__input" id="qa_name" name="qa_name" />
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">QA Title</label>
                    <input v-model="form.qa_title" type="text" class="rform__input" id="qa_title" name="qa_title" />
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">QA Email</label>
                    <input v-model="form.qa_email" type="email" class="rform__input"
                      :class="{ 'is-invalid': fieldError('qa_email') }" id="qa_email" name="qa_email" />
                    <span v-if="fieldError('qa_email')" class="rform__error">{{ fieldError('qa_email') }}</span>
                  </div>
                </div>
                <div class="rform__row">
                  <div class="rform__field">
                    <label class="rform__label">QA Phone</label>
                    <input v-model="form.qa_phone" type="tel" class="rform__input" id="qa_phone" name="qa_phone" />
                  </div>
                </div>
              </div>

              <!-- Compliance Questionnaire -->
              <div class="section-block section-block--compliance"
                :class="{ 'section-block--has-error': sectionHasError(complianceErrorKeys) }">
                <div class="section-head">
                  <span class="section-head__icon">📋</span>
                  <div>
                    <h4 class="section-head__title">Regulatory Compliance Questionnaire</h4>
                    <p class="section-head__desc">Indicate YES / NO / N/A and provide remarks where applicable.</p>
                  </div>
                </div>

                <div v-for="q in questions" :key="q.key" class="q-row"
                  :class="{ 'q-row--error': fieldError(`compliance.${q.key}.answer`) || fieldError(`compliance.${q.key}.remarks`) }">
                  <div class="q-num">{{ q.num }}</div>
                  <div class="q-content">
                    <div class="q-header">
                      <div class="q-text" v-html="q.text"></div>
                      <div class="yn-group">
                        <label v-for="opt in q.opts" :key="opt" class="yn-label" :class="ynClass(opt)">
                          <input type="radio" :name="`compliance[${q.key}][answer]`" :value="opt"
                            v-model="compliance[q.key].answer" :id="`compliance_${q.key}_${opt}`" />
                          {{ ynIcon(opt) }} {{ opt }}
                        </label>
                      </div>
                    </div>
                    <span v-if="fieldError(`compliance.${q.key}.answer`)" class="rform__error">
                      {{ fieldError(`compliance.${q.key}.answer`) }}
                    </span>
                    <ul v-if="q.sub && q.sub.length" class="q-sub">
                      <li v-for="(s, i) in q.sub" :key="i">{{ s }}</li>
                    </ul>
                    <div v-if="q.has_remarks === 'yes' && compliance[q.key].answer === 'NO'"
                      class="rform__field supplier-signup__remarks">
                      <textarea v-model="compliance[q.key].remarks" class="rform__textarea" rows="1"
                        placeholder="Remarks..." :class="{ 'is-invalid': fieldError(`compliance.${q.key}.remarks`) }"
                        :id="`compliance_${q.key}_remarks`" :name="`compliance[${q.key}][remarks]`"></textarea>
                      <span v-if="fieldError(`compliance.${q.key}.remarks`)" class="rform__error">
                        {{ fieldError(`compliance.${q.key}.remarks`) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- IP Certifications -->
              <div class="section-block section-block--ipcert">
                <div class="section-head">
                  <span class="section-head__icon">🌱</span>
                  <div>
                    <h4 class="section-head__title">Identity Preserved Certifications</h4>
                    <p class="section-head__desc">Certifications, standards, or claims you maintain.</p>
                  </div>
                </div>
                <div class="rform__field">
                  <div class="rform__radio-group">
                    <label class="rform__radio">
                      <input type="radio" value="yes" v-model="ipCertOption" name="ip_cert_option"
                        id="ip_cert_option_yes" />
                      <span class="rform__radio-dot"></span>
                      <span class="rform__radio-text">Yes</span>
                    </label>
                    <label class="rform__radio">
                      <input type="radio" value="na" v-model="ipCertOption" name="ip_cert_option"
                        id="ip_cert_option_na" />
                      <span class="rform__radio-dot"></span>
                      <span class="rform__radio-text">N/A</span>
                    </label>
                  </div>
                  <span v-if="fieldError('ip_cert_option')" class="rform__error">{{ fieldError('ip_cert_option')
                    }}</span>
                </div>

                <template v-if="ipCertOption === 'yes'">
                  <p class="rform__help rform__intro mt-2">Check all that apply and attach the certificate file.</p>
                  <div class="ip-grid">
                    <div v-for="c in ipCerts" :key="c.key" class="ip-item">
                      <label class="rform__checkbox">
                        <input type="checkbox" v-model="ipCertsForm[c.key].selected"
                          :name="`ip_certs[${c.key}][selected]`" :id="`ip_cert_${c.key}`" />
                        <span class="rform__checkbox-box"></span>
                        <span class="rform__checkbox-text">{{ c.icon }} {{ c.label }}</span>
                      </label>
                      <div v-if="ipCertsForm[c.key].selected" class="ip-file-zone">
                        <div v-if="ipCertsForm[c.key].existingFile && !ipCertsForm[c.key].file" class="current-file">
                          <a :href="ipCertsForm[c.key].existingFile.url" target="_blank" class="current-file__link">
                            📎 {{ ipCertsForm[c.key].existingFile.name }}
                          </a>
                          <span class="current-file__label">on file</span>
                        </div>
                        <label class="rform__label">{{ ipCertsForm[c.key].existingFile ? 'Replace Certificate File' :
                          'Attach Certificate File' }}</label>
                        <input type="file" class="rform__input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          @change="e => (ipCertsForm[c.key].file = e.target.files[0])"
                          :name="`ip_certs[${c.key}][file]`" :id="`ip_cert_${c.key}_file`" />
                        <span v-if="fieldError(`ip_certs.${c.key}.file`)" class="rform__error">
                          {{ fieldError(`ip_certs.${c.key}.file`) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div v-for="(extra, idx) in ipExtraCerts" :key="extra.id"
                    class="rform__row supplier-signup__extra-row">
                    <div class="rform__field mt-3">
                      <label class="rform__label">Certification Name</label>
                      <input v-model="extra.title" type="text" class="rform__input" placeholder="e.g. Fairtrade"
                        :name="`ip_certs[${extra.id}][title]`" :id="`ip_cert_${extra.id}_title`" />
                    </div>
                    <div class="rform__field mt-3">
                      <label class="rform__label">{{ extra.existingFile ? 'Replace File' : 'Attach File' }}</label>
                      <div v-if="extra.existingFile && !extra.file" class="current-file">
                        <a :href="extra.existingFile.url" target="_blank" class="current-file__link">📎 {{
                          extra.existingFile.name }}</a>
                      </div>
                      <input type="file" class="rform__input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        @change="e => (extra.file = e.target.files[0])" :name="`ip_certs[${extra.id}][file]`"
                        :id="`ip_cert_${extra.id}_file`" />
                    </div>
                    <button type="button"
                      class="rform__btn mt-3 rform__btn--ghost rform__btn--ghost-danger rform__btn--sm supplier-signup__remove-btn"
                      @click="removeIpExtra(idx)">
                      Remove
                    </button>
                  </div>
                  <div class="rform__btn-wrap">
                    <button type="button" class="rform__btn--add rform__btn--add-sm" @click="addIpExtra">Add
                      More</button>
                  </div>
                </template>
              </div>

              <!-- Food Safety Certification -->
              <div class="section-block section-block--foodsafety"
                :class="{ 'section-block--has-error': fieldError('foodSafetyCert') || fieldError('certificates') }">
                <div class="section-head">
                  <span class="section-head__icon">🛡️</span>
                  <div>
                    <h4 class="section-head__title">Food Safety Management Certification</h4>
                    <p class="section-head__desc">Do you maintain an accredited food safety certification?</p>
                  </div>
                </div>
                <div class="rform__field">
                  <div class="rform__radio-group">
                    <label class="rform__radio">
                      <input type="radio" value="yes" v-model="foodSafetyCert" name="foodSafetyCert"
                        id="food_safety_cert_yes" />
                      <span class="rform__radio-dot"></span>
                      <span class="rform__radio-text">Yes</span>
                    </label>
                    <label class="rform__radio">
                      <input type="radio" value="no" v-model="foodSafetyCert" name="foodSafetyCert"
                        id="food_safety_cert_no" />
                      <span class="rform__radio-dot"></span>
                      <span class="rform__radio-text">No</span>
                    </label>
                  </div>
                  <span v-if="fieldError('foodSafetyCert')" class="rform__error">{{ fieldError('foodSafetyCert')
                    }}</span>
                </div>

                <template v-if="foodSafetyCert === 'yes'">
                  <div v-for="(cert, idx) in selectedCertificates" :key="cert.id" class="cert-card"
                    :class="{ 'cert-card--error': fieldError(`certificates.${certificateKey(cert)}`) }">
                    <div class="cert-card__header">
                      <strong>{{ cert.isOther ? (cert.title || 'Other Certificate') : cert.value }}</strong>
                      <button type="button" class="rform__remove-btn rform__remove-btn--sm"
                        @click="removeCertificate(idx)">Remove</button>
                    </div>
                    <span v-if="fieldError(`certificates.${certificateKey(cert)}`)" class="rform__error">
                      {{ fieldError(`certificates.${certificateKey(cert)}`) }}
                    </span>
                    <div class="rform__row">
                      <div v-if="cert.isOther" class="rform__field">
                        <label class="rform__label rform__label--required">Certificate Title</label>
                        <input v-model="cert.title" type="text" class="rform__input"
                          :class="{ 'is-invalid': fieldError(`certificates.${certificateKey(cert)}.title`) }"
                          placeholder="e.g. My Custom Certificate"
                          :name="`certificates[${certificateKey(cert)}][title]`" />
                      </div>
                      <div class="rform__field">
                        <label class="rform__label" :class="{ 'rform__label--required': !cert.existingFile }">
                          {{ cert.existingFile ? 'Replace Certificate File' : 'Certificate File' }}
                        </label>
                        <div v-if="cert.existingFile && !cert.file" class="current-file">
                          <a :href="cert.existingFile.url" target="_blank" class="current-file__link">📎 {{
                            cert.existingFile.name }}</a>
                        </div>
                        <input type="file" class="rform__input"
                          :class="{ 'is-invalid': fieldError(`certificates.${certificateKey(cert)}.file`) }"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="e => (cert.file = e.target.files[0])"
                          :name="`certificates[${certificateKey(cert)}][file]`" />
                        <span v-if="fieldError(`certificates.${certificateKey(cert)}.file`)" class="rform__error">
                          {{ fieldError(`certificates.${certificateKey(cert)}.file`) }}
                        </span>
                      </div>
                      <div class="rform__field">
                        <label class="rform__label rform__label--required">Expiry Date</label>
                        <input v-model="cert.expiryDate" type="date" class="rform__input"
                          :class="{ 'is-invalid': fieldError(`certificates.${certificateKey(cert)}.expiryDate`) }"
                          :name="`certificates[${certificateKey(cert)}][expiry_date]`" />
                      </div>
                      <div class="rform__field">
                        <label class="rform__label">Comment</label>
                        <input v-model="cert.comment" type="text" class="rform__input" placeholder="Optional notes"
                          :name="`certificates[${certificateKey(cert)}][comment]`" />
                      </div>
                    </div>
                  </div>

                  <div class="rform__row--2 mt-3">
                    <div class="rform__field">
                      <label class="rform__label">Add a certificate</label>
                      <div class="rform__select-wrap">
                        <select v-model="certToAdd" class="rform__select mt-2" @change="addCertificate" id="cert_to_add"
                          name="cert_to_add">
                          <option value="">— Select a certificate to add —</option>
                          <optgroup label="GFSI Certifications">
                            <option value="SQF">SQF</option>
                            <option value="BRCGS">BRCGS</option>
                            <option value="FSSC 22000">FSSC 22000</option>
                            <option value="IFS">IFS</option>
                          </optgroup>
                          <optgroup label="Other Food Safety Certifications">
                            <option value="Global G.A.P.">Global G.A.P.</option>
                            <option value="Canada G.A.P.">Canada G.A.P.</option>
                            <option value="ISO 22000">ISO 22000</option>
                            <option value="HACCP">HACCP</option>
                            <option value="GMP">GMP</option>
                            <option value="CFIA Licence">CFIA Licence</option>
                            <option value="FSMA Compliance Audit">FSMA Compliance Audit</option>
                            <option value="PrimusGFS">PrimusGFS</option>
                          </optgroup>
                          <optgroup label="Custom">
                            <option value="__other__">Other (specify title)</option>
                          </optgroup>
                        </select>
                      </div>
                    </div>
                  </div>
                  <span v-if="fieldError('certificates')" class="rform__error">{{ fieldError('certificates') }}</span>
                  <p v-if="!selectedCertificates.length" class="rform__help supplier-signup__intro mt-2">
                    No certificates added yet. Select one above.
                  </p>
                </template>

                <template v-else-if="foodSafetyCert === 'no'">
                  <div class="section-subhead">
                    <span class="section-subhead__icon">📝</span>
                    <div>
                      <h5 class="section-subhead__title">Non-Certified Questionnaire</h5>
                      <p class="section-subhead__desc">Please indicate YES/NO and provide detail where requested.</p>
                    </div>
                  </div>
                  <span v-if="fieldError('non_cert')" class="rform__error">{{ fieldError('non_cert') }}</span>

                  <div v-for="q in nonCertQuestions" :key="q.key" class="q-row"
                    :class="{ 'q-row--error': fieldError(`non_cert.${q.key}.answer`) || fieldError(`non_cert.${q.key}.remarks`) }">
                    <div class="q-num">{{ q.num }}</div>
                    <div class="q-content">
                      <div class="q-header">
                        <div class="q-text">{{ q.text }}</div>
                        <div class="yn-group">
                          <label class="yn-label yn-yes">
                            <input type="radio" value="YES" v-model="nonCert[q.key].answer"
                              :name="`non_cert[${q.key}][answer]`" />✓ YES
                          </label>
                          <label class="yn-label yn-no">
                            <input type="radio" value="NO" v-model="nonCert[q.key].answer"
                              :name="`non_cert[${q.key}][answer]`" />✕ NO
                          </label>
                        </div>
                      </div>
                      <span v-if="fieldError(`non_cert.${q.key}.answer`)" class="rform__error">
                        {{ fieldError(`non_cert.${q.key}.answer`) }}
                      </span>

                      <div v-if="q.has_details && nonCert[q.key].answer === 'YES'"
                        class="rform__row--2 supplier-signup__details-row">
                        <div class="rform__field">
                          <label class="rform__label">{{ q.details_label }}</label>
                          <textarea v-if="q.details_type === 'textarea'" v-model="nonCert[q.key].details"
                            class="rform__textarea" :placeholder="q.details_placeholder"
                            :name="`non_cert[${q.key}][details]`"></textarea>
                          <input v-else v-model="nonCert[q.key].details" :type="q.details_type" class="rform__input"
                            :placeholder="q.details_placeholder" :name="`non_cert[${q.key}][details]`" />
                        </div>
                        <div v-if="q.has_second_detail" class="rform__field">
                          <label class="rform__label">{{ q.second_label }}</label>
                          <input v-model="nonCert[q.key].second_detail" :type="q.second_type" class="rform__input"
                            :placeholder="q.second_placeholder" :name="`non_cert[${q.key}][second_detail]`" />
                        </div>
                      </div>

                      <div v-if="q.has_remarks && nonCert[q.key].answer === 'NO'"
                        class="rform__field supplier-signup__remarks">
                        <label class="rform__label" :class="{ 'rform__label--required': q.remarks_required }">
                          {{ q.remarks_label || 'Remarks' }}
                        </label>
                        <textarea v-if="q.remarks_type === 'textarea'" v-model="nonCert[q.key].remarks"
                          class="rform__textarea" :rows="q.remarks_rows || 3"
                          :placeholder="q.remarks_placeholder || 'Please provide remarks...'"
                          :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.remarks`) }"
                          :name="`non_cert[${q.key}][remarks]`"></textarea>
                        <input v-else v-model="nonCert[q.key].remarks" type="text" class="rform__input"
                          :placeholder="q.remarks_placeholder || 'Please provide remarks...'"
                          :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.remarks`) }"
                          :name="`non_cert[${q.key}][remarks]`" />
                        <span v-if="fieldError(`non_cert.${q.key}.remarks`)" class="rform__error">
                          {{ fieldError(`non_cert.${q.key}.remarks`) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Comments -->
              <div class="section-block section-block--comments">
                <div class="section-head">
                  <span class="section-head__icon">💬</span>
                  <div>
                    <h4 class="section-head__title">Anything else?</h4>
                    <p class="section-head__desc">Optional — add any additional context.</p>
                  </div>
                </div>
                <div class="rform__field">
                  <textarea v-model="form.comments" class="rform__textarea" rows="4"
                    placeholder="Write comments here..." id="comments" name="comments"></textarea>
                </div>
              </div>
            </ReusableForm>
          </section>

          <!-- ================================================================
               TAB 2 — Offerings (Materials, Products, Services)
               ================================================================ -->
          <section v-show="activeTab === 'offerings'" class="offerings-section" >
            <!-- Materials Form -->
            <div class="section-block section-block--materials" v-if=" businessTypes.includes('supplier')">
              <div class="section-head">
                <span class="section-head__icon">📦</span>
                <div>
                  <h4 class="section-head__title">Materials</h4>
                  <p class="section-head__desc">Add each material you supply or produce.</p>
                  <span class="section-head__tag section-head__tag--materials">🌾 Suppliers only</span>
                </div>
              </div>

              <ReusableForm id="materials_form" :loading="submitting.materials" submit-label="Save Materials"
                :show-cancel="false" @submit.prevent="handleMaterialsSubmit">
                <div v-if="topError.materials" class="rform__error supplier-signup__top-error">
                  <i class="fa fa-triangle-exclamation"></i>
                  {{ topError.materials }}
                </div>

                <!-- Draft entry form -->
                <div class="draft-card" :class="{ 'draft-card--editing': editingMaterialIndex !== null }">
                  <div class="draft-card__label" v-if="editingMaterialIndex !== null">
                    <i class="fa fa-pencil"></i> Editing material — update the fields and click "Update Material"
                  </div>

                  <div class="rform__row mt-3">
                    <div class="rform__field">
                      <label class="rform__label rform__label--required">Name</label>
                      <input v-model="materialDraft.name" type="text" class="rform__input"
                        placeholder="e.g. Organic Cane Sugar" />
                    </div>
                    <div class="rform__field">
                      <label class="rform__label rform__label--required">Type</label>
                      <div class="rform__select-wrap">
                        <select v-model="materialDraft.type" class="rform__select">
                          <option value="">Select type</option>
                          <option value="raw_material">Raw Material</option>
                          <option value="packaging">Packaging Material</option>
                          <option value="finished_material">Finished Material</option>
                        </select>
                      </div>
                    </div>
                    <div class="rform__field">
                      <label class="rform__label">Lead Time</label>
                      <input v-model="materialDraft.lead_time" type="text" class="rform__input"
                        placeholder="e.g. 4-6 weeks" />
                    </div>
                  </div>

                  <div class="rform__row mt-3">
                    <div class="rform__field">
                      <label class="rform__label">Packaging Size</label>
                      <input v-model="materialDraft.packaging_size" type="text" class="rform__input"
                        placeholder="e.g. 25 kg bag" />
                    </div>
                    <div class="rform__field">
                      <label class="rform__label">Packaging Type</label>
                      <div class="rform__select-wrap">
                        <select v-model="materialDraft.packaging_type" class="rform__select">
                          <option value="">Select packaging type</option>
                          <option value="glass">Glass</option>
                          <option value="plastic">Plastic</option>
                          <option value="paper_box">Paper Box</option>
                          <option value="metal">Metal</option>
                        </select>
                      </div>
                    </div>
                    <div class="rform__field">
                      <label class="rform__label">Upload Documents</label>
                      <input type="file" class="rform__input" multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                        @change="e => (materialDraft.files = Array.from(e.target.files))" />
                      <span v-if="materialDraft.files.length" class="rform__help">
                        {{ materialDraft.files.length }} file(s) selected
                      </span>
                    </div>
                  </div>

                  <div class="row py-3 px-1 mt-2">
                    <div class="col-md-6">
                    <div class="rform__field">
  <label class="rform__label">Allergens List</label>
  <MultiSelect
    v-model="materialDraft.allergens"
    :options="allergensOptions"
    :multiple="true"
    option-label="name"
    option-value="id"
    placeholder="Select allergens..."
    id="edit_material_allergens"
  />
</div>
                    </div>
                    <div class="col-md-6">
                      <div class="rform__field">
                        <label class="rform__label">Sub Ingredients</label>
                        <textarea v-model="materialDraft.sub_ingredients" class="rform__textarea" rows="2"
                          placeholder="List sub-ingredients, comma separated"></textarea>
                      </div>
                    </div>
                  </div>

                  <span v-if="materialDraftError" class="rform__error">{{ materialDraftError }}</span>

                  <div class="draft-card__actions">
                    <button v-if="editingMaterialIndex !== null" type="button"
                      class="rform__btn rform__btn--ghost" @click="cancelMaterialEdit">
                      Cancel Edit
                    </button>
                    <button type="button" class="rform__btn--add" @click="addOrUpdateMaterial">
                      <i :class="editingMaterialIndex !== null ? 'fa fa-check' : 'fa fa-plus'"></i>
                      {{ editingMaterialIndex !== null ? 'Update Material' : 'Add to List' }}
                    </button>
                  </div>
                </div>

                <!-- Added materials table -->
                <div v-if="materials.length" class="added-table-wrap">
                  <table class="added-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Packaging</th>
                        <th>Lead Time</th>
                        <th class="added-table__actions-col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(material, idx) in materials" :key="material.id"
                        :class="{ 'is-editing': editingMaterialIndex === idx }">
                        <td class="added-table__strong">{{ material.name }}</td>
                        <td>{{ formatSlug(material.type) }}</td>
                        <td>
                          {{ material.packaging_size || '—' }}
                          <span v-if="material.packaging_type"> · {{ formatSlug(material.packaging_type) }}</span>
                        </td>
                        <td>{{ material.lead_time || '—' }}</td>
                        <td class="added-table__actions">
                          <button type="button" class="table-icon-btn" @click="editMaterial(idx)"
                            aria-label="Edit material">
                            <i class="fa fa-pencil"></i>
                          </button>
                          <button type="button" class="table-icon-btn table-icon-btn--danger"
                            @click="removeMaterial(idx)" aria-label="Remove material">
                            <i class="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="rform__help rform__intro">No materials added yet — fill the form above and click "Add to
                  List".</p>
                <span v-if="fieldError('materials')" class="rform__error">{{ fieldError('materials') }}</span>
              </ReusableForm>
            </div>

            <!-- Products Form -->
            <div class="section-block section-block--products" v-if="businessTypes.includes('manufacturer')">
              <div class="section-head">
                <span class="section-head__icon">🧴</span>
                <div>
                  <h4 class="section-head__title">Products</h4>
                  <p class="section-head__desc">Add each product you manufacture.</p>
                  <span class="section-head__tag section-head__tag--products">🏭 Manufacturers only</span>
                </div>
              </div>

              <ReusableForm id="products_form" :loading="submitting.products" submit-label="Save Products"
                :show-cancel="false" @submit.prevent="handleProductsSubmit">
                <div v-if="topError.products" class="rform__error supplier-signup__top-error">
                  <i class="fa fa-triangle-exclamation"></i>
                  {{ topError.products }}
                </div>

                <div class="draft-card" :class="{ 'draft-card--editing': editingProductIndex !== null }">
                  <div class="draft-card__label" v-if="editingProductIndex !== null">
                    <i class="fa fa-pencil"></i> Editing product — update the fields and click "Update Product"
                  </div>

                  <div class="rform__row mt-3">
                    <div class="rform__field">
                      <label class="rform__label rform__label--required">Name</label>
                      <input v-model="productDraft.name" type="text" class="rform__input"
                        placeholder="e.g. Organic Trail Mix" />
                    </div>
                    <div class="rform__field">
                      <label class="rform__label">Product Application</label>
                      <div class="rform__select-wrap">
                        <select v-model="productDraft.application" class="rform__select">
                          <option value="">Select application type</option>
                          <option value="retail">Retail</option>
                          <option value="wholesale">Wholesale</option>
                          <option value="food_service">Food Service</option>
                          <option value="institutional_sale">Institutional Sale</option>
                        </select>
                      </div>
                    </div>
                    <div class="rform__field">
                      <label class="rform__label">Product Code / SKU</label>
                      <input v-model="productDraft.sku" type="text" class="rform__input"
                        placeholder="e.g. TM-1024" />
                    </div>
                  </div>

                  <div class="rform__row mt-3">
                    <div class="rform__field">
                      <label class="rform__label rform__label--required">Production Type</label>
                      <div class="rform__select-wrap">
                        <select v-model="productDraft.production_type" class="rform__select">
                          <option value="">--Select--</option>
                          <option value="wip">WIP (Work In Progress)</option>
                          <option value="fp">FP (Finish Product)</option>
                        </select>
                      </div>
                    </div>
                    <div class="rform__field">
                      <label class="rform__label rform__label--required">Unit</label>
                      <input v-model="productDraft.unit" type="text" class="rform__input"
                        placeholder="e.g. kg, box, case" />
                    </div>
                  </div>

                  <div class="rform__row mt-3">
                    <div class="rform__field" style="grid-column: 1 / -1;">
                      <label class="rform__label">Product Description</label>
                      <textarea v-model="productDraft.description" class="rform__textarea" rows="2"
                        placeholder="Optional — add any details about this product..."></textarea>
                    </div>
                  </div>

                  <span v-if="productDraftError" class="rform__error">{{ productDraftError }}</span>

                  <div class="draft-card__actions">
                    <button v-if="editingProductIndex !== null" type="button"
                      class="rform__btn rform__btn--ghost" @click="cancelProductEdit">
                      Cancel Edit
                    </button>
                    <button type="button" class="rform__btn--add" @click="addOrUpdateProduct">
                      <i :class="editingProductIndex !== null ? 'fa fa-check' : 'fa fa-plus'"></i>
                      {{ editingProductIndex !== null ? 'Update Product' : 'Add to List' }}
                    </button>
                  </div>
                </div>

                <div v-if="products.length" class="added-table-wrap">
                  <table class="added-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Application</th>
                        <th>Production Type</th>
                        <th>Unit</th>
                        <th class="added-table__actions-col">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, idx) in products" :key="product.id"
                        :class="{ 'is-editing': editingProductIndex === idx }">
                        <td class="added-table__strong">{{ product.name }}</td>
                        <td>{{ formatSlug(product.application) || '—' }}</td>
                        <td>{{ productionTypeLabel(product.production_type) }}</td>
                        <td>{{ product.unit }}</td>
                        <td class="added-table__actions">
                          <button type="button" class="table-icon-btn" @click="editProduct(idx)"
                            aria-label="Edit product">
                            <i class="fa fa-pencil"></i>
                          </button>
                          <button type="button" class="table-icon-btn table-icon-btn--danger"
                            @click="removeProduct(idx)" aria-label="Remove product">
                            <i class="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="rform__help rform__intro">No products added yet — fill the form above and click "Add to
                  List".</p>
                <span v-if="fieldError('products')" class="rform__error">{{ fieldError('products') }}</span>
              </ReusableForm>
            </div>

            <!-- Services Form -->
            <div class="section-block section-block--services" v-if="businessTypes.includes('service_provider')">
              <div class="section-head">
                <span class="section-head__icon">🤝</span>
                <div>
                  <h4 class="section-head__title">Services Offered</h4>
                  <p class="section-head__desc">Select each category of service you provide.</p>
                  <span class="section-head__tag section-head__tag--services">🤝 Service Providers only</span>
                </div>
              </div>

              <ReusableForm id="services_form" :loading="submitting.services" submit-label="Save Services"
                :show-cancel="false" @submit.prevent="handleServicesSubmit">
                <div v-if="topError.services" class="rform__error supplier-signup__top-error">
                  <i class="fa fa-triangle-exclamation"></i>
                  {{ topError.services }}
                </div>

                <div class="rform__field">
                      <label class="rform__label">Services</label>
                      <MultiSelect
                        v-model="servicesSelected"
                        :options="serviceOptions"
                        :multiple="true"
                        option-label="name"
                        option-value="id"
                        placeholder="Select services..."
                        id="services_selected"
                      />
                      <span v-if="fieldError('services')" class="rform__error">
                        {{ fieldError('services') }}
                      </span>
                    </div>
                <div v-if="hasOtherServiceSelected" class="rform__field">
                  <label class="rform__label rform__label--required">Specify other service</label>
                  <input v-model="servicesOtherLabel" type="text" class="rform__input"
                    :class="{ 'is-invalid': fieldError('services.other_label') }"
                    placeholder="e.g. Cold-chain logistics" />
                  <span v-if="fieldError('services.other_label')" class="rform__error">
                    {{ fieldError('services.other_label') }}
                  </span>
                </div>
              </ReusableForm>
            </div>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ReusableForm from '@/components/utils/Form.vue'
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'

export default {
  name: 'EditBasicInformation',
  components: { ReusableForm, MultiSelect },

  emits: ['updated'],

  data() {
    return {
      loading: true,
      loadError: '',
      submitting: {
        basic: false,
        materials: false,
        products: false,
        services: false,
      },
      topError: {
        basic: '',
        materials: '',
        products: '',
        services: '',
      },
      errors: {},

      activeTab: 'basic',

      businessTypes: [],
      businessTypeOptions: [
        { value: 'supplier', icon: '🌾', label: 'Supplier', desc: 'You supply raw materials or ingredients.' },
        { value: 'manufacturer', icon: '🏭', label: 'Manufacturer', desc: 'You produce or process finished goods.' },
        { value: 'service_provider', icon: '🤝', label: 'Service Provider', desc: 'You offer logistics, QA, or other services.' },
      ],

      questions: [],
      ipCerts: [],
      nonCertQuestions: [],
      allergensOptions: [],
      serviceOptions: [],

      form: {
        company_name: '',
        email: '',
        phone: '',
        manufacturing_type: '',
        product_brand_type: '',
        external_type: '',
        physical_address: '',
        street_city: '',
        state: '',
        postal_code: '',
        logo: null,
        emergency_contact: '',
        poc: '',
        title: '',
        qa_name: '',
        qa_title: '',
        qa_email: '',
        qa_phone: '',
        production_city: '',
        production_state: '',
        production_address: '',
        production_postcode: '',
        comments: '',
      },
      existingLogoUrl: '',

      compliance: {},
      ipCertOption: 'na',
      ipCertsForm: {},
      ipExtraCerts: [],
      ipExtraCounter: 0,

      foodSafetyCert: '',
      certToAdd: '',
      selectedCertificates: [],
      certCounter: 0,
      addedCertValues: new Set(),

      nonCert: {},

      // Materials
      materials: [],
      materialCounter: 0,
      materialDraft: this.emptyMaterialDraft(),
      editingMaterialIndex: null,
      materialDraftError: '',

      // Products
      products: [],
      productCounter: 0,
      productDraft: this.emptyProductDraft(),
      editingProductIndex: null,
      productDraftError: '',

      // Services
      servicesSelected: [],
      servicesOtherLabel: '',
    }
  },

  computed: {
    tabs() {
      return [
        { key: 'basic', label: 'Basic Information', icon: '🏢' },
        { key: 'offerings', label: 'Offerings', icon: '📦', count: this.materials.length + this.products.length + this.servicesSelected.length },
      ]
    },
    complianceErrorKeys() {
      const keys = []
      this.questions.forEach((q) => keys.push(`compliance.${q.key}.answer`, `compliance.${q.key}.remarks`))
      return keys
    },
    hasOtherServiceSelected() {
      return this.servicesSelected.some((s) => (s?.id ?? s) === 'other')
    },
  },

  async mounted() {
    await this.loadQues()
    await this.loadProfile()
  },

  methods: {
    fieldError(key) {
      const value = this.errors ? this.errors[key] : null
      if (!value) return ''
      return Array.isArray(value) ? value[0] : value
    },
    sectionHasError(keys) {
      return keys.some((k) => !!this.fieldError(k))
    },
    formatSlug(str) {
      if (!str || typeof str !== 'string') return ''
      return str.split('_').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    },
    productionTypeLabel(value) {
      if (value === 'wip') return 'WIP (Work In Progress)'
      if (value === 'fp') return 'FP (Finish Product)'
      return '—'
    },
    getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },

    // Service helper methods
    getServiceName(service) {
      if (!service) return ''
      if (typeof service === 'object' && service.name) return service.name
      if (typeof service === 'object' && service.id) {
        const found = this.serviceOptions.find(opt => opt.id === service.id)
        return found ? found.name : service.id
      }
      return service
    },

    removeService(service) {
      const index = this.servicesSelected.findIndex(s => {
        if (typeof s === 'object' && s.id) {
          return s.id === (typeof service === 'object' ? service.id : service)
        }
        return s === service
      })
      if (index > -1) {
        this.servicesSelected.splice(index, 1)
      }
    },

    emptyMaterialDraft() {
      return {
        id: null,
        name: '',
        type: '',
        packaging_size: '',
        packaging_type: '',
        lead_time: '',
        sub_ingredients: '',
        allergens: [],
        files: []
      }
    },
    emptyProductDraft() {
      return { id: null, name: '', application: '', sku: '', production_type: '', unit: '', description: '' }
    },

    async loadQues() {
      const result = await form_api_submit({
        url: 'supplier/create_supplier',
        method: 'GET',
        show_success_toast: false,
        show_error_toast: false,
      })
      if (!result?.success) return

      const data = result.data || {}
      this.questions = data.questions || []
      this.ipCerts = data.ipCerts || []
      this.nonCertQuestions = data.nonCertQuestions || []
      this.allergensOptions = data.allergens || []
      this.serviceOptions = data.services || []

      this.questions.forEach((q) => {
        this.compliance[q.key] = { answer: '', remarks: '' }
      })
      this.ipCerts.forEach((c) => {
        this.ipCertsForm[c.key] = { selected: false, file: null, existingFile: null }
      })
      this.nonCertQuestions.forEach((q) => {
        this.nonCert[q.key] = { answer: '', details: '', second_detail: '', remarks: '' }
      })
    },

async loadProfile() {
  this.loading = true
  this.loadError = ''

  const result = await form_api_submit({
    url: 'supplier/my-profile',
    method: 'GET',
    show_success_toast: false,
    show_error_toast: false,
  })

  if (!result?.success) {
    this.loadError = 'Could not load your profile. Please try again.'
    this.loading = false
    return
  }

  const data = result.data || {}
  const supplier = data.supplier || {}

  // Get current business types
  const currentTypes = (data.supplier_types || []).map((t) => t.name)
  this.businessTypes = currentTypes

  // If manufacturer is not selected, clear manufacturing fields
  const isManufacturer = currentTypes.includes('manufacturer')
  const isSupplier = currentTypes.includes('supplier')
  const isServiceProvider = currentTypes.includes('service_provider')

  this.form = {
    company_name: supplier.company_name || '',
    email: supplier.email || '',
    phone: supplier.phone || '',
    // Only show manufacturing fields if manufacturer type is selected
    manufacturing_type: isManufacturer ? (supplier.manufacture_site || '') : '',
    product_brand_type: isManufacturer ? (supplier.manufacture_category || '') : '',
    external_type: isManufacturer ? (supplier.manufacture_type || '') : '',
    physical_address: supplier.physical_address || '',
    street_city: supplier.street_city || '',
    state: supplier.state || '',
    postal_code: supplier.postal_code || '',
    logo: null,
    emergency_contact: supplier.emergency_contact || '',
    poc: supplier.POC || supplier.poc || '',
    title: supplier.title || '',
    qa_name: supplier.qa_name || '',
    qa_title: supplier.qa_title || '',
    qa_email: supplier.qa_email || '',
    qa_phone: supplier.qa_phone || '',
    // Only show production fields if supplier type is selected
    production_city: isSupplier ? (supplier.production_city || '') : '',
    production_state: isSupplier ? (supplier.production_state || '') : '',
    production_address: isSupplier ? (supplier.production_address || '') : '',
    production_postcode: isSupplier ? (supplier.production_postcode || '') : '',
    comments: supplier.comments || '',
  }
  this.existingLogoUrl = supplier.photo ? this.getFileUrl(supplier.photo) : ''

  // Compliance
  ;(data.questionnaire_responses || []).forEach((r) => {
    const key = r.question_key || r.key
    if (key && this.compliance[key]) {
      this.compliance[key] = { answer: r.response || '', remarks: r.remarks || '' }
    }
  })

  // Non-cert
  let hasNonCertResponses = false
  ;(data.non_cert_responses || []).forEach((r) => {
    const key = r.question_key || r.key
    if (!key || !this.nonCert[key]) return
    hasNonCertResponses = true
    let details = ''
    let secondDetail = ''
    if (r.sub_items_response) {
      try {
        const parsed = JSON.parse(r.sub_items_response)
        if (parsed && typeof parsed === 'object') {
          const values = Object.values(parsed)
          details = values[0] || ''
          secondDetail = values[1] || ''
        } else {
          details = r.sub_items_response
        }
      } catch (e) {
        details = r.sub_items_response
      }
    }
    this.nonCert[key] = { answer: r.response || '', details, second_detail: secondDetail, remarks: r.remarks || '' }
  })

  this.foodSafetyCert = supplier.food_safety_cert || (hasNonCertResponses ? 'no' : '')

  // Certificates
  this.selectedCertificates = (data.certificates || []).map((c) => {
    const id = `cert_existing_${c.id}`
    const knownValues = ['SQF', 'BRCGS', 'FSSC 22000', 'IFS', 'Global G.A.P.', 'Canada G.A.P.', 'ISO 22000', 'HACCP', 'GMP', 'CFIA Licence', 'FSMA Compliance Audit', 'PrimusGFS']
    const matched = knownValues.find((v) => v.toLowerCase() === (c.document_name || '').toLowerCase())
    const isOther = !matched
    if (!isOther) this.addedCertValues.add(matched)
    return {
      id,
      value: matched || '__other__',
      isOther,
      title: isOther ? c.document_name || '' : '',
      file: null,
      existingFile: c.document ? { url: this.getFileUrl(c.document), name: c.document_name || c.document.split('/').pop() } : null,
      expiryDate: c.expiry_date || '',
      comment: c.description || '',
    }
  })

  // IP Certs
  const ipDocs = data.ip_certificates || []
  if (ipDocs.length) this.ipCertOption = 'yes'
  ipDocs.forEach((doc) => {
    const matchedConfig = this.ipCerts.find(
      (c) => c.label.toLowerCase() === (doc.document_name || '').toLowerCase()
    )
    const existingFile = doc.document
      ? { url: this.getFileUrl(doc.document), name: doc.document_name || doc.document.split('/').pop() }
      : null
    if (matchedConfig) {
      this.ipCertsForm[matchedConfig.key] = { selected: true, file: null, existingFile }
    } else {
      this.ipExtraCerts.push({
        id: `extra_existing_${doc.id}`,
        title: doc.document_name || '',
        file: null,
        existingFile,
      })
    }
  })

  // Materials - only show if supplier type is selected
  if (isSupplier) {
    this.materials = (data.materials || []).map((material) => {
      const allergenObjects = (material.get_allergens || []).map((allergen) => ({
        id: allergen.id,
        name: allergen.name
      }))

      return {
        id: material.id || `material_${this.materialCounter++}`,
        name: material.name || '',
        type: material.type || '',
        packaging_size: material.pack_size || '',
        packaging_type: material.pack_type || '',
        lead_time: material.lead_time || '',
        sub_ingredients: material.sub_ingredients || '',
        allergens: allergenObjects,
        files: [],
        _original: material
      }
    })
  } else {
    this.materials = []
  }

  // Products - only show if manufacturer type is selected
  if (isManufacturer) {
    this.products = (data.products || []).map((product) => ({
      id: product.id || `product_${this.productCounter++}`,
      name: product.name || '',
      application: product.application || '',
      sku: product.sku || '',
      production_type: product.production_type || '',
      unit: product.unit || '',
      description: product.description || '',
    }))
  } else {
    this.products = []
  }

  // Services - only show if service_provider type is selected
  if (isServiceProvider) {
    this.servicesSelected = Array.isArray(data.services)
      ? data.services.map((s) => (typeof s === 'object' ? s.id : s))
      : []
  } else {
    this.servicesSelected = []
  }

  this.loading = false
},

    onSimpleFile(e, key) {
      this.form[key] = e.target.files[0] || null
    },

    addIpExtra() {
      this.ipExtraCerts.push({ id: `extra_${this.ipExtraCounter++}`, title: '', file: null, existingFile: null })
    },
    removeIpExtra(idx) {
      this.ipExtraCerts.splice(idx, 1)
    },

    certificateKey(cert) {
      return cert.isOther ? cert.id : cert.value.replace(/[^a-zA-Z0-9]/g, '_')
    },
    addCertificate() {
      const value = this.certToAdd
      if (!value) return
      const isOther = value === '__other__'
      if (!isOther && this.addedCertValues.has(value)) {
        this.certToAdd = ''
        return
      }
      this.selectedCertificates.push({
        id: `cert_${this.certCounter++}`,
        value,
        isOther,
        title: '',
        file: null,
        existingFile: null,
        expiryDate: '',
        comment: '',
      })
      if (!isOther) this.addedCertValues.add(value)
      this.certToAdd = ''
    },
    removeCertificate(idx) {
      const cert = this.selectedCertificates[idx]
      if (cert && !cert.isOther) this.addedCertValues.delete(cert.value)
      this.selectedCertificates.splice(idx, 1)
    },

    ynClass(opt) {
      if (opt === 'YES') return 'yn-yes'
      if (opt === 'NO') return 'yn-no'
      return 'yn-na'
    },
    ynIcon(opt) {
      if (opt === 'YES') return '✓'
      if (opt === 'NO') return '✕'
      return '—'
    },

    // Materials
    addOrUpdateMaterial() {
      this.materialDraftError = ''
      if (!this.materialDraft.name || !this.materialDraft.type) {
        this.materialDraftError = 'Name and Type are required.'
        return
      }

      // Prepare the material data with proper allergen IDs
      const materialData = {
        ...this.materialDraft,
        // Extract just the IDs from allergen objects for storage
        allergen_ids: this.materialDraft.allergens.map(a => a.id || a)
      }

      if (this.editingMaterialIndex !== null) {
        this.materials.splice(this.editingMaterialIndex, 1, {
          ...materialData,
          id: this.materials[this.editingMaterialIndex].id || `material_${this.materialCounter++}`
        })
        this.editingMaterialIndex = null
      } else {
        this.materials.push({
          ...materialData,
          id: `material_${this.materialCounter++}`
        })
      }
      this.materialDraft = this.emptyMaterialDraft()
    },

  editMaterial(idx) {
  this.editingMaterialIndex = idx
  const material = this.materials[idx]

  this.materialDraft = {
    id: material.id || null,
    name: material.name || '',
    type: material.type || '',
    packaging_size: material.packaging_size || '',
    packaging_type: material.packaging_type || '',
    lead_time: material.lead_time || '',
    sub_ingredients: material.sub_ingredients || '',
    allergens: Array.isArray(material.allergens)
      ? material.allergens.map(a => typeof a === 'object' ? a.id : a)
      : [],
    files: Array.isArray(material.files) ? [...material.files] : []
  }

  this.materialDraftError = ''
},

    cancelMaterialEdit() {
      this.editingMaterialIndex = null
      this.materialDraft = this.emptyMaterialDraft()
      this.materialDraftError = ''
    },

    removeMaterial(idx) {
      this.materials.splice(idx, 1)
      if (this.editingMaterialIndex === idx) this.cancelMaterialEdit()
    },

    // Products
    addOrUpdateProduct() {
      this.productDraftError = ''
      if (!this.productDraft.name || !this.productDraft.production_type || !this.productDraft.unit) {
        this.productDraftError = 'Name, Production Type, and Unit are required.'
        return
      }
      if (this.editingProductIndex !== null) {
        this.products.splice(this.editingProductIndex, 1, { ...this.productDraft })
        this.editingProductIndex = null
      } else {
        this.products.push({ ...this.productDraft, id: `product_${this.productCounter++}` })
      }
      this.productDraft = this.emptyProductDraft()
    },

    editProduct(idx) {
      this.editingProductIndex = idx
      this.productDraft = { ...this.products[idx] }
      this.productDraftError = ''
    },

    cancelProductEdit() {
      this.editingProductIndex = null
      this.productDraft = this.emptyProductDraft()
      this.productDraftError = ''
    },

    removeProduct(idx) {
      this.products.splice(idx, 1)
      if (this.editingProductIndex === idx) this.cancelProductEdit()
    },

    // ---- Form submission methods ----
 buildBasicFormData() {
  const fd = new FormData()
  
  const formData = {
    company_name: this.form.company_name,
    email: this.form.email,
    phone: this.form.phone,
    manufacturing_type: this.form.manufacturing_type,
    product_brand_type: this.form.product_brand_type,
    external_type: this.form.external_type,
    physical_address: this.form.physical_address,
    street_city: this.form.street_city,
    state: this.form.state,
    postal_code: this.form.postal_code,
    emergency_contact: this.form.emergency_contact,
    poc: this.form.poc,
    title: this.form.title,
    qa_name: this.form.qa_name,
    qa_title: this.form.qa_title,
    qa_email: this.form.qa_email,
    qa_phone: this.form.qa_phone,
    production_city: this.form.production_city,
    production_state: this.form.production_state,
    production_address: this.form.production_address,
    production_postcode: this.form.production_postcode,
    comments: this.form.comments,
    foodSafetyCert: this.foodSafetyCert,
    ip_cert_option: this.ipCertOption,
  }

  // Append all form data
  Object.entries(formData).forEach(([key, value]) => {
    if (value === null || value === undefined) return
    fd.append(key, value instanceof File ? value : String(value))
  })

  // Append logo if it's a file
  if (this.form.logo instanceof File) {
    fd.append('photo', this.form.logo)
  }

  // Append business types
  this.businessTypes.forEach((t) => fd.append('types[]', t))

  // ============================================================
  // IMPORTANT: Send removed types to API so it can clean up
  // ============================================================
  // Track which types are being removed
  const removedTypes = []
  if (!this.businessTypes.includes('manufacturer')) {
    removedTypes.push('manufacturer')
  }
  if (!this.businessTypes.includes('supplier')) {
    removedTypes.push('supplier')
  }
  if (!this.businessTypes.includes('service_provider')) {
    removedTypes.push('service_provider')
  }
  
  // Send removed types to API for cleanup
  removedTypes.forEach((type) => {
    fd.append('removed_types[]', type)
  })

  // Append compliance
  Object.entries(this.compliance).forEach(([key, entry]) => {
    fd.append(`compliance[${key}][answer]`, entry.answer)
    fd.append(`compliance[${key}][remarks]`, entry.remarks)
  })

  // Append IP certs
  if (this.ipCertOption === 'yes') {
    Object.entries(this.ipCertsForm).forEach(([key, entry]) => {
      fd.append(`ip_certs[${key}][selected]`, entry.selected ? '1' : '')
      if (entry.file) fd.append(`ip_certs[${key}][file]`, entry.file)
    })
    this.ipExtraCerts.forEach((extra) => {
      fd.append(`ip_certs[${extra.id}][title]`, extra.title)
      if (extra.file) fd.append(`ip_certs[${extra.id}][file]`, extra.file)
    })
  }

  // Append certificates
  if (this.foodSafetyCert === 'yes') {
    this.selectedCertificates.forEach((cert) => {
      const key = this.certificateKey(cert)
      fd.append(`certificates[${key}][title]`, cert.isOther ? cert.title : cert.value)
      if (cert.file) fd.append(`certificates[${key}][file]`, cert.file)
      fd.append(`certificates[${key}][expiry_date]`, cert.expiryDate)
      fd.append(`certificates[${key}][comment]`, cert.comment)
    })
  } else if (this.foodSafetyCert === 'no') {
    Object.entries(this.nonCert).forEach(([key, entry]) => {
      fd.append(`non_cert[${key}][answer]`, entry.answer)
      fd.append(`non_cert[${key}][details]`, entry.details)
      fd.append(`non_cert[${key}][second_detail]`, entry.second_detail)
      fd.append(`non_cert[${key}][remarks]`, entry.remarks)
    })
  }

  return fd
},

    buildMaterialsFormData() {
      const fd = new FormData()
      this.materials.forEach((material) => {
        const key = material.id
        fd.append(`materials[${key}][name]`, material.name || '')
        fd.append(`materials[${key}][type]`, material.type || '')
        // Use packaging_size as pack_size for the API
        fd.append(`materials[${key}][pack_size]`, material.packaging_size || '')
        // Use packaging_type as pack_type for the API
        fd.append(`materials[${key}][pack_type]`, material.packaging_type || '')
        fd.append(`materials[${key}][lead_time]`, material.lead_time || '')
        fd.append(`materials[${key}][sub_ingredients]`, material.sub_ingredients || '')

        // Send allergen IDs
        const allergenIds = (material.allergens || []).map(a => a.id || a)
        allergenIds.forEach((id) => {
          if (id) {
            fd.append(`materials[${key}][allergen_ids][]`, id)
          }
        })

        // Handle files
        if (material.files && material.files.length) {
          material.files.forEach((file, fIdx) => {
            if (file instanceof File) {
              fd.append(`materials[${key}][files][${fIdx}]`, file)
            }
          })
        }
      })
      return fd
    },

    buildProductsFormData() {
      const fd = new FormData()
      this.products.forEach((product) => {
        const key = product.id
        fd.append(`products[${key}][name]`, product.name)
        fd.append(`products[${key}][application]`, product.application)
        fd.append(`products[${key}][sku]`, product.sku)
        fd.append(`products[${key}][production_type]`, product.production_type)
        fd.append(`products[${key}][unit]`, product.unit)
        fd.append(`products[${key}][description]`, product.description)
      })
      return fd
    },
buildServicesFormData() {
  const fd = new FormData()

  this.servicesSelected.forEach((id) => {
    if (id) {
      fd.append('service_ids[]', id)
    }
  })

  if (this.hasOtherServiceSelected) {
    fd.append('services_other_label', this.servicesOtherLabel)
  }

  return fd
},

    validateBasic() {
      this.errors = {}
      let isValid = true

      if (!this.businessTypes.length) {
        this.errors.types = 'Select at least one option.'
        isValid = false
      }

      this.questions.forEach((q) => {
        const entry = this.compliance[q.key]
        if (!entry.answer) {
          this.errors[`compliance.${q.key}.answer`] = 'Please answer this question.'
          isValid = false
          return
        }
        if (q.has_remarks === 'yes' && entry.answer === 'NO' && !entry.remarks.trim()) {
          this.errors[`compliance.${q.key}.remarks`] = 'Remarks are required when answering NO.'
          isValid = false
        }
      })

      if (!this.foodSafetyCert) {
        this.errors.foodSafetyCert = 'Please select Yes or No.'
        isValid = false
      }

      if (this.foodSafetyCert === 'yes') {
        if (!this.selectedCertificates.length) {
          this.errors.certificates = 'Please add at least one certificate.'
          isValid = false
        }
        this.selectedCertificates.forEach((cert) => {
          const key = this.certificateKey(cert)
          if (cert.isOther && !cert.title.trim()) {
            this.errors[`certificates.${key}.title`] = 'Certificate title is required.'
            isValid = false
          }
          if (!cert.file && !cert.existingFile) {
            this.errors[`certificates.${key}.file`] = 'Certificate file is required.'
            isValid = false
          }
          if (!cert.expiryDate) {
            this.errors[`certificates.${key}.expiryDate`] = 'Certificate expiry date is required.'
            isValid = false
          }
        })
      }

      if (this.foodSafetyCert === 'no') {
        this.nonCertQuestions.forEach((q) => {
          const entry = this.nonCert[q.key]
          if (!entry.answer) {
            this.errors[`non_cert.${q.key}.answer`] = 'Please answer this question.'
            isValid = false
            return
          }
          if (q.has_remarks && entry.answer === 'NO' && (q.remarks_required ?? true) && !entry.remarks.trim()) {
            this.errors[`non_cert.${q.key}.remarks`] = q.remarks_placeholder || 'Remarks are required when answering NO.'
            isValid = false
          }
        })
      }

      return isValid
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const el = document.querySelector('.is-invalid, .rform__error, .q-row--error, .section-block--has-error')
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    async handleBasicSubmit() {
      if (!this.validateBasic()) {
        this.topError.basic = 'Please fix the highlighted fields before saving.'
        this.scrollToFirstError()
        return
      }
      this.submitting.basic = true
      this.errors = {}
      this.topError.basic = ''

      const result = await form_api_submit({
        url: 'supplier/update-basic-info',
        method: 'POST',
        data: this.buildBasicFormData(),
        curr_form_id: '#edit_basic_info_form',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: true,
      })

      if (result?.success) {
        this.$emit('updated', result.data)
        await this.loadProfile()
      } else if (result?.data?.error_type === 'validation') {
        this.errors = result.data.errors
        this.topError.basic = 'Please fix the highlighted fields before saving.'
        this.scrollToFirstError()
      }

      this.submitting.basic = false
    },

    async handleMaterialsSubmit() {
      this.submitting.materials = true
      this.topError.materials = ''

      const result = await form_api_submit({
        url: 'supplier/update-materials',
        method: 'POST',
        data: this.buildMaterialsFormData(),
        curr_form_id: '#materials_form',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: true,
      })

      if (result?.success) {
        this.$emit('updated', result.data)
        await this.loadProfile()
      } else if (result?.data?.error_type === 'validation') {
        this.errors = result.data.errors
        this.topError.materials = 'Please fix the highlighted fields.'
        this.scrollToFirstError()
      }

      this.submitting.materials = false
    },

    async handleProductsSubmit() {
      this.submitting.products = true
      this.topError.products = ''

      const result = await form_api_submit({
        url: 'supplier/update-products',
        method: 'POST',
        data: this.buildProductsFormData(),
        curr_form_id: '#products_form',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: true,
      })

      if (result?.success) {
        this.$emit('updated', result.data)
        await this.loadProfile()
      } else if (result?.data?.error_type === 'validation') {
        this.errors = result.data.errors
        this.topError.products = 'Please fix the highlighted fields.'
        this.scrollToFirstError()
      }

      this.submitting.products = false
    },

    async handleServicesSubmit() {
      this.submitting.services = true
      this.topError.services = ''

      const result = await form_api_submit({
        url: 'supplier/update-services',
        method: 'POST',
        data: this.buildServicesFormData(),
        curr_form_id: '#services_form',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: true,
      })

      if (result?.success) {
        this.$emit('updated', result.data)
        await this.loadProfile()
      } else if (result?.data?.error_type === 'validation') {
        this.errors = result.data.errors
        this.topError.services = 'Please fix the highlighted fields.'
        this.scrollToFirstError()
      }

      this.submitting.services = false
    },
  },
}
</script>

<style scoped>
.section-block--materials {
  border-left: 5px solid #457b9d;
  border-top: 1px solid #457b9d;
}

.section-block--products {
  border-left: 5px solid #d1495b;
  border-top: 1px solid #d1495b;
}

.section-block--services {
  border-left: 5px solid #e07a5f;
  border-top: 1px solid #e07a5f;
}

.section-head__tag--materials {
  background: rgba(69, 123, 157, 0.12);
  border: 1px solid rgba(69, 123, 157, 0.35);
  color: #2d5773;
}

.section-head__tag--products {
  background: rgba(209, 73, 91, 0.12);
  border: 1px solid rgba(209, 73, 91, 0.35);
  color: #a5293d;
}

.section-head__tag--services {
  background: rgba(224, 122, 95, 0.12);
  border: 1px solid rgba(224, 122, 95, 0.35);
  color: #b8492f;
}

/* ----- Draft cards ----- */
.draft-card {
  background: rgba(255, 255, 255, 0.55);
  border: 1.5px dashed var(--color-border);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 18px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.draft-card--editing {
  border-style: solid;
  border-color: var(--color-secondary);
  background: rgba(236, 153, 58, 0.05);
}

.draft-card__label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(236, 153, 58, 0.12);
  color: #b5670f;
  font-size: 12.5px;
  font-weight: 700;
}

.draft-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

/* ----- Added items table ----- */
.added-table-wrap {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  margin-bottom: 8px;
}

.added-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.added-table thead th {
  text-align: left;
  padding: 11px 14px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-muted);
  background: var(--color-tint-light);
  border-bottom: 1px solid var(--color-border);
}

.added-table__actions-col {
  width: 96px;
  text-align: right !important;
}

.added-table tbody td {
  padding: 11px 14px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-dark);
  vertical-align: middle;
}

.added-table tbody tr:last-child td {
  border-bottom: none;
}

.added-table tbody tr {
  transition: background 0.15s ease;
}

.added-table tbody tr:hover td {
  background: var(--color-tint-light);
}

.added-table tbody tr.is-editing td {
  background: rgba(236, 153, 58, 0.1);
}

.added-table__strong {
  font-weight: 700;
}

.added-table__actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}

/* ----- Table action buttons ----- */
.table-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.table-icon-btn:hover {
  background: var(--color-tint-light);
  color: var(--color-text-dark);
  transform: translateY(-1px);
}

.table-icon-btn--danger:hover {
  background: rgba(179, 38, 30, 0.1);
  color: #b3261e;
}

/* ----- Add button ----- */
.rform__btn--add {
  height: 35px;
  padding: 0 22px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 8px 22px -10px rgba(93, 28, 106, 0.5);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.rform__btn--add:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px -10px rgba(93, 28, 106, 0.62);
  filter: brightness(1.04);
}

.rform__btn--add:active {
  transform: scale(0.97);
}

.rform__btn-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

/* ----- Selected Services Tags ----- */
.selected-services {
  margin: 12px 0 8px;
}

.selected-services__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
}

.selected-services__tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 14px;
  background: #f0f1ff;
  border: 1px solid #c7d2fe;
  border-radius: 20px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 500;
}

.selected-services__remove {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0 2px;
  font-size: 14px;
  display: flex;
  align-items: center;
  transition: color 0.15s ease;
}

.selected-services__remove:hover {
  color: #dc2626;
}

/* ----- Edit tabs ----- */
.edit-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--color-border);
  overflow-x: auto;
  background: rgba(255, 255, 255, 0.5);
  padding: 4px 4px 0;
  border-radius: 14px 14px 0 0;
}

.edit-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: none;
  background: transparent;
  border-bottom: 3px solid transparent;
  color: var(--color-text-muted);
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, border-color 0.15s ease;
  border-radius: 8px 8px 0 0;
}

.edit-tab:hover {
  color: var(--color-text-dark);
  background: rgba(255, 255, 255, 0.5);
}

.edit-tab.is-active {
  color: var(--color-secondary);
  border-bottom-color: var(--color-secondary);
  background: rgba(255, 255, 255, 0.8);
}

.edit-tab__icon {
  font-size: 15px;
}

.edit-tab__count {
  background: var(--color-tint-light);
  color: var(--color-text-dark);
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
}

/* ----- Hero edit variant ----- */
.hero--edit {
  max-width: 720px;
}

/* ----- Offerings section spacing ----- */
.offerings-section .section-block:last-child {
  margin-bottom: 0;
}

/* ----- Responsive ----- */
@media (max-width: 760px) {
  .added-table {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }

  .edit-tabs {
    gap: 4px;
  }

  .edit-tab {
    padding: 10px 12px;
    font-size: 12px;
  }
}

@media (max-width: 640px) {
  .draft-card {
    padding: 14px;
  }

  .draft-card__actions {
    flex-direction: column;
  }

  .draft-card__actions .rform__btn--add {
    width: 100%;
  }
}
</style>